import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FlowMark } from '@/components/ui/FlowMark';

const TONES = {
  warning: {
    tile: 'bg-amber-50 ring-amber-200/70 dark:bg-amber-900/30 dark:ring-amber-700/40',
    icon: 'text-amber-600 dark:text-amber-400',
  },
  danger: {
    tile: 'bg-red-50 ring-red-200/70 dark:bg-red-900/30 dark:ring-red-700/40',
    icon: 'text-red-600 dark:text-red-400',
  },
} as const;

/**
 * The shared shell for every "you can't get in yet" screen: one icon, one
 * sentence, and the actions that actually recover — plus optional small print
 * naming the failure, so a support conversation can start from a fact instead
 * of "it just says it's loading".
 *
 * Lives in its own module so the dev fixture gallery can render each entry
 * state without an account (see src/dev/authFixtures.tsx). Every screen in
 * Flow sits behind sign-in, and the sign-in gate itself was the least
 * inspectable of all — which is how it shipped telling locked-out users to
 * check their connection.
 */
export function GateCard({
  title,
  body,
  detail,
  actions,
  icon: Icon = AlertTriangle,
  tone = 'warning',
  children,
}: {
  title: string;
  body: ReactNode;
  detail?: string;
  actions: ReactNode;
  icon?: LucideIcon;
  tone?: keyof typeof TONES;
  /** Further paragraphs between the body and the actions. */
  children?: ReactNode;
}) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-6 bg-canvas p-4">
      <div className="flex items-center gap-2.5">
        <FlowMark size={28} title={null} />
        <span className="text-base font-bold tracking-tight text-gray-900 dark:text-gray-100">Flow</span>
      </div>
      <div className="max-w-md w-full bg-white dark:bg-gray-900 rounded-2xl shadow-card border border-card-border p-8 text-center">
        <div
          className={cn(
            'w-12 h-12 rounded-xl ring-1 ring-inset flex items-center justify-center mx-auto mb-4',
            TONES[tone].tile
          )}
        >
          <Icon className={cn('h-6 w-6', TONES[tone].icon)} aria-hidden="true" />
        </div>
        <h1 className="text-lg font-semibold tracking-tight text-gray-900 dark:text-gray-100 mb-2">{title}</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 text-balance">{body}</p>
        {children}
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">{actions}</div>
        {detail && <p className="mt-6 text-xs text-gray-400 dark:text-gray-500">{detail}</p>}
      </div>
    </div>
  );
}

/** The full-page wait shown while the gate makes up its mind. */
export function GateSpinner({ label }: { label: string }) {
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center gap-5 bg-canvas p-4"
      role="status"
      aria-live="polite"
    >
      <FlowMark size={40} title={null} className="animate-pulse" />
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
    </div>
  );
}
